import { useEffect, useRef } from 'react';

interface ShortcutItem {
  keys: string[];
  description: string;
}

interface ShortcutGroup {
  label: string;
  icon: string;
  items: ShortcutItem[];
}

const IS_MAC = typeof navigator !== 'undefined' && /Mac|iPhone|iPad/.test(navigator.platform);
const MOD = IS_MAC ? '⌘' : 'Ctrl';

const GROUPS: ShortcutGroup[] = [
  {
    label: 'Search',
    icon: 'ri-search-line',
    items: [
      { keys: [MOD, 'K'], description: 'Focus search bar' },
      { keys: ['/'], description: 'Focus search bar (outside inputs)' },
      { keys: ['Enter'], description: 'Run search' },
      { keys: [MOD, 'Enter'], description: 'Run search from anywhere' },
    ],
  },
  {
    label: 'Results & Filters',
    icon: 'ri-filter-3-line',
    items: [
      { keys: [MOD, 'F'], description: 'Focus results filter bar' },
      { keys: [MOD, '⇧', 'A'], description: 'Toggle Advanced Search panel' },
      { keys: ['Esc'], description: 'Clear results filter' },
    ],
  },
  {
    label: 'Panels',
    icon: 'ri-layout-right-line',
    items: [
      { keys: [MOD, '⇧', 'L'], description: 'Open Saved Library' },
      { keys: ['Esc'], description: 'Close open panel / modal' },
      { keys: ['?'], description: 'Toggle this shortcuts guide' },
    ],
  },
];

interface Props {
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
}

export default function KeyboardShortcutsLegend({ open, onToggle, onClose }: Props) {
  const panelRef = useRef<HTMLDivElement>(null);

  /* close on outside click */
  useEffect(() => {
    if (!open) return;
    const handle = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [open, onClose]);

  return (
    <div ref={panelRef} className="fixed bottom-6 right-6 z-50 flex flex-col items-end gap-3">
      {/* Popup panel */}
      <div
        className={`bg-white border border-slate-200 rounded-2xl w-80 transition-all duration-200 origin-bottom-right ${
          open
            ? 'opacity-100 scale-100 translate-y-0 pointer-events-auto'
            : 'opacity-0 scale-95 translate-y-2 pointer-events-none'
        }`}
        style={{ boxShadow: '0 8px 40px rgba(0,0,0,0.12)' }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-gradient-to-r from-slate-800 to-slate-700 rounded-t-2xl">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-md bg-white/20 flex items-center justify-center">
              <i className="ri-keyboard-line text-white text-sm"></i>
            </div>
            <span className="text-sm font-bold text-white">Keyboard Shortcuts</span>
          </div>
          <button
            onClick={onClose}
            className="w-6 h-6 flex items-center justify-center rounded-full hover:bg-white/10 transition-colors cursor-pointer"
          >
            <i className="ri-close-line text-white text-sm"></i>
          </button>
        </div>

        {/* Groups */}
        <div className="p-3 space-y-3 max-h-96 overflow-y-auto">
          {GROUPS.map((group) => (
            <div key={group.label}>
              <div className="flex items-center gap-1.5 mb-1.5 px-1">
                <i className={`${group.icon} text-slate-400 text-xs`}></i>
                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
                  {group.label}
                </span>
              </div>
              <div className="space-y-1">
                {group.items.map((item, i) => (
                  <div
                    key={i}
                    className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg hover:bg-slate-50 transition-colors"
                  >
                    <span className="text-xs text-slate-600 flex-1">{item.description}</span>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {item.keys.map((key, ki) => (
                        <span key={ki} className="flex items-center gap-1">
                          <kbd className="inline-flex items-center justify-center min-w-[24px] h-6 px-1.5 bg-slate-100 border border-slate-300 rounded-md text-[11px] font-bold text-slate-700 font-mono leading-none">
                            {key}
                          </kbd>
                          {ki < item.keys.length - 1 && (
                            <span className="text-[10px] text-slate-400">+</span>
                          )}
                        </span>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="px-4 py-2.5 border-t border-slate-100 bg-slate-50 rounded-b-2xl">
          <p className="text-[10px] text-slate-400 text-center">
            Press <kbd className="px-1 py-0.5 bg-white border border-slate-200 rounded text-[10px] font-mono font-bold text-slate-600">?</kbd> to toggle this panel
          </p>
        </div>
      </div>

      {/* Trigger button */}
      <button
        onClick={onToggle}
        title="Keyboard shortcuts (?)"
        className={`w-11 h-11 rounded-full border-2 flex items-center justify-center transition-all cursor-pointer ${
          open
            ? 'bg-slate-800 border-slate-800 text-white'
            : 'bg-white border-slate-300 text-slate-600 hover:border-slate-500 hover:text-slate-800'
        }`}
        style={{ boxShadow: '0 2px 12px rgba(0,0,0,0.10)' }}
      >
        <i className="ri-keyboard-line text-lg"></i>
      </button>
    </div>
  );
}
